import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';

export type ActionKeyId =
  | 'moveUp'
  | 'moveDown'
  | 'moveLeft'
  | 'moveRight'
  | 'attack'
  | 'inventory'
  | 'pickup'
  | 'toggleMenu'
  | 'equip'
  | 'characterInfo'
  | 'dropItem'
  | 'passiveTree'
  | 'spells'
  | 'skills'
  | 'spell1'
  | 'spell2'
  | 'spell3'
  | 'spell4'
  | 'spell5'
  | 'spell6'
  | 'spell7'
  | 'spell8'
  | 'spell9'
  | 'spell10'
  | 'meditate'
  | 'clearTarget'
  | 'cycleTarget'
  | 'toggleMap'
  | 'toggleBoat'
  | 'cycleSummonMode'
  | 'useItem';

export interface SavedKeyBinding {
  primary?: number;
  secondary?: number;
}

export const ACTION_KEY_IDS: ActionKeyId[] = [
  'moveUp',
  'moveDown',
  'moveLeft',
  'moveRight',
  'attack',
  'inventory',
  'pickup',
  'toggleMenu',
  'equip',
  'characterInfo',
  'dropItem',
  'passiveTree',
  'spells',
  'skills',
  'spell1',
  'spell2',
  'spell3',
  'spell4',
  'spell5',
  'spell6',
  'spell7',
  'spell8',
  'spell9',
  'spell10',
  'meditate',
  'clearTarget',
  'cycleTarget',
  'toggleMap',
  'toggleBoat',
  'cycleSummonMode',
  'useItem',
];

export const createDefaultKeyBindings = (): Record<ActionKeyId, SavedKeyBinding> => ({
  moveUp: { primary: Phaser.Input.Keyboard.KeyCodes.UP },
  moveDown: { primary: Phaser.Input.Keyboard.KeyCodes.DOWN },
  moveLeft: { primary: Phaser.Input.Keyboard.KeyCodes.LEFT },
  moveRight: { primary: Phaser.Input.Keyboard.KeyCodes.RIGHT },
  attack: { primary: Phaser.Input.Keyboard.KeyCodes.CTRL },
  equip: { primary: Phaser.Input.Keyboard.KeyCodes.E },
  inventory: { primary: Phaser.Input.Keyboard.KeyCodes.I },
  pickup: { primary: Phaser.Input.Keyboard.KeyCodes.A },
  toggleMenu: { primary: Phaser.Input.Keyboard.KeyCodes.ESC },
  characterInfo: { primary: Phaser.Input.Keyboard.KeyCodes.C },
  dropItem: { primary: Phaser.Input.Keyboard.KeyCodes.T },
  passiveTree: { primary: Phaser.Input.Keyboard.KeyCodes.P },
  spells: { primary: Phaser.Input.Keyboard.KeyCodes.H },
  skills: { primary: Phaser.Input.Keyboard.KeyCodes.K },
  spell1: { primary: Phaser.Input.Keyboard.KeyCodes.ONE },
  spell2: { primary: Phaser.Input.Keyboard.KeyCodes.TWO },
  spell3: { primary: Phaser.Input.Keyboard.KeyCodes.THREE },
  spell4: { primary: Phaser.Input.Keyboard.KeyCodes.FOUR },
  spell5: { primary: Phaser.Input.Keyboard.KeyCodes.FIVE },
  spell6: { primary: Phaser.Input.Keyboard.KeyCodes.SIX },
  spell7: { primary: Phaser.Input.Keyboard.KeyCodes.SEVEN },
  spell8: { primary: Phaser.Input.Keyboard.KeyCodes.EIGHT },
  spell9: { primary: Phaser.Input.Keyboard.KeyCodes.NINE },
  spell10: { primary: Phaser.Input.Keyboard.KeyCodes.ZERO },
  meditate: { primary: Phaser.Input.Keyboard.KeyCodes.Y },
  clearTarget: { primary: Phaser.Input.Keyboard.KeyCodes.BACKTICK },
  cycleTarget: { primary: Phaser.Input.Keyboard.KeyCodes.TAB },
  toggleMap: { primary: Phaser.Input.Keyboard.KeyCodes.M },
  toggleBoat: { primary: Phaser.Input.Keyboard.KeyCodes.N },
  cycleSummonMode: { primary: Phaser.Input.Keyboard.KeyCodes.G },
  useItem: { primary: Phaser.Input.Keyboard.KeyCodes.R },
});

const createKeyCodeNameMap = (): Record<number, string> => {
  const map: Record<number, string> = {};
  Object.entries(Phaser.Input.Keyboard.KeyCodes).forEach(([name, value]) => {
    if (typeof value === 'number') {
      map[value] = name;
    }
  });
  return map;
};

const KEY_CODE_NAME_MAP = createKeyCodeNameMap();

export class InputManager {
  private scene: Phaser.Scene;

  private keyBindingConfig: Record<ActionKeyId, SavedKeyBinding>;

  private actionKeys: Partial<Record<ActionKeyId, Phaser.Input.Keyboard.Key[]>> = {};

  constructor(scene: Phaser.Scene, initialBindings?: Partial<Record<ActionKeyId, SavedKeyBinding>>) {
    this.scene = scene;
    this.keyBindingConfig = createDefaultKeyBindings();

    if (initialBindings) {
      this.applySavedBindings(initialBindings);
    }
  }

  applySavedBindings(bindings: Partial<Record<ActionKeyId, SavedKeyBinding>>): void {
    ACTION_KEY_IDS.forEach((action) => {
      const entry = bindings[action];
      if (!entry) return;
      if (typeof entry.primary === 'number') {
        this.keyBindingConfig[action].primary = entry.primary;
      }
      if ('secondary' in entry) {
        this.keyBindingConfig[action].secondary = entry.secondary;
      }
    });
  }

  configureActionKeys(): void {
    const keyboard = this.scene.input.keyboard;
    if (!keyboard) return;

    ACTION_KEY_IDS.forEach((action) => {
      const binding = this.keyBindingConfig[action];
      const keys: Phaser.Input.Keyboard.Key[] = [];
      if (typeof binding.primary === 'number') {
        keys.push(keyboard.addKey(binding.primary));
      }
      if (typeof binding.secondary === 'number') {
        keys.push(keyboard.addKey(binding.secondary));
      }
      this.actionKeys[action] = keys;
    });
  }

  getDirectionFromInput(): Direction | null {
    if (this.isActionDown('moveUp')) return Direction.UP;
    if (this.isActionDown('moveDown')) return Direction.DOWN;
    if (this.isActionDown('moveLeft')) return Direction.LEFT;
    if (this.isActionDown('moveRight')) return Direction.RIGHT;
    return null;
  }

  isActionJustDown(action: ActionKeyId): boolean {
    const keys = this.actionKeys[action];
    if (!keys || keys.length === 0) return false;
    return keys.some((key) => Phaser.Input.Keyboard.JustDown(key));
  }

  isActionDown(action: ActionKeyId): boolean {
    const keys = this.actionKeys[action];
    if (!keys || keys.length === 0) return false;
    return keys.some((key) => key.isDown);
  }

  isActionKeyCode(action: ActionKeyId, keyCode?: number): boolean {
    if (keyCode == null) return false;
    const binding = this.keyBindingConfig[action];
    return Boolean(binding && (binding.primary === keyCode || binding.secondary === keyCode));
  }

  getKeyName(keyCode?: number): string {
    if (typeof keyCode !== 'number') return 'Sin asignar';
    return KEY_CODE_NAME_MAP[keyCode] ?? `Key ${keyCode}`;
  }

  getActionKeyLabel(action: ActionKeyId): string {
    const binding = this.keyBindingConfig[action];
    if (!binding) return 'Sin asignar';
    const parts: string[] = [];
    if (typeof binding.primary === 'number') {
      parts.push(this.getKeyName(binding.primary));
    }
    if (typeof binding.secondary === 'number') {
      parts.push(this.getKeyName(binding.secondary));
    }
    return parts.length > 0 ? parts.join(' / ') : 'Sin asignar';
  }

  getKeyBindingsForStorage(): Record<ActionKeyId, SavedKeyBinding> {
    const payload = {} as Record<ActionKeyId, SavedKeyBinding>;
    ACTION_KEY_IDS.forEach((action) => {
      const binding = this.keyBindingConfig[action];
      payload[action] = {
        primary: binding.primary,
        secondary: binding.secondary,
      };
    });
    return payload;
  }

  getBinding(action: ActionKeyId): SavedKeyBinding {
    return this.keyBindingConfig[action];
  }

  setBinding(action: ActionKeyId, slot: 'primary' | 'secondary', keyCode?: number): void {
    const binding = this.keyBindingConfig[action];
    if (!binding) return;
    if (slot === 'primary') {
      binding.primary = keyCode;
    } else {
      binding.secondary = keyCode;
    }
  }
}

